import React, { useEffect, useMemo, useState } from 'react'
import Popup, { PopupSize } from '../../../components/popup/Popup'
import { AppInput } from '../../../components/AppInput/AppInput'
import AppBtn, { AppBtnColor, AppBtnSize } from '../../../components/btn/AppBtn'
import {
  useAddCategoryMutation,
  useGetCategoryByIdQuery,
  useUpdateCategoryMutation,
} from '../../../core/api/CategoryApi'
import {
  AppNotification,
  NotificationType,
} from '../../../components/notification/Notification'
import { useGetCurrentUserQuery } from '../../../core/api/UserApi'
import { useTranslation } from 'react-i18next'
import { IEdgeServers } from '../../../core/api/dto/BuffetDishDto'
import { StationEditList } from '../../../entities/StationEditList/StationEditList'
import { useUpdateCategoryStations } from './hooks/useUpdateCategoryStations'

interface IAddCategoryPopupProps {
  id?: number | null
  closePopup: () => void
}

export const AddCategoryPopup = ({
  closePopup,
  id,
}: IAddCategoryPopupProps) => {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [error, setError] = useState('')
  const { data: currentUser } = useGetCurrentUserQuery()
  const { data: currentCategory } = useGetCategoryByIdQuery(id ? id : 0, {
    skip: !id,
  })
  const [currentEdgeServers, setCurrentEdgeServers] = useState<IEdgeServers[]>(
    [],
  )
  const { onUpdateStation } = useUpdateCategoryStations(
    currentCategory?.edgeServers || [],
    currentEdgeServers,
  )
  const [createCategory, { isLoading: isLoadingCreate }] =
    useAddCategoryMutation()
  const [updateCategory, { isLoading: isLoadingUpdate }] =
    useUpdateCategoryMutation()
  const isDisabled: boolean = useMemo(
    () =>
      !!currentCategory?.isSynced &&
      process.env.REACT_APP_TYPE_SERVER !== 'MainServer',
    [currentCategory?.isSynced],
  )

  useEffect(() => {
    if (!!currentCategory) {
      setTitle(currentCategory.name)
      setCurrentEdgeServers(currentCategory?.edgeServers || [])
    }
  }, [currentCategory])

  const onSaveHandler = () => {
    if (!title) {
      setError('Обязательное поле')
      return
    }
    if (!!id) {
      updateCategory({ id, name: title })
        .unwrap()
        .then((res) => {
          onUpdateStation(res.guid)
          AppNotification({
            msg: t('Категория успешно обновлена!'),
            type: NotificationType.success,
          })
          closePopup()
        })
    } else {
      createCategory({
        name: title,
        restaurant: currentUser?.userRoles?.restaurant?.id,
      })
        .unwrap()
        .then((res) => {
          onUpdateStation(res.guid)
          AppNotification({
            msg: t('Категория успешно создана!'),
            type: NotificationType.success,
          })
          closePopup()
        })
    }
  }

  return (
    <Popup
      size={PopupSize.base}
      borderRadiusMin
      closePopup={closePopup}
      isLoading={isLoadingCreate || isLoadingUpdate}
    >
      <div className="category-popup">
        <h3>{t(`${!!id ? 'Редактирование' : 'Создание'} категории блюда`)}</h3>
        <AppInput
          value={title}
          onChange={(value) => {
            setError('')
            setTitle(value)
          }}
          label="Название"
          border
          fullWidth
          error={error ? error : undefined}
          disabled={isDisabled}
        />
        {process.env.REACT_APP_TYPE_SERVER === 'MainServer' && (
          <StationEditList
            currentEdgeServers={currentEdgeServers}
            setCurrentEdgeServers={setCurrentEdgeServers}
          />
        )}
        <div className="buttons-block">
          <AppBtn
            title={'Отменить'}
            color={AppBtnColor.outlineViolet}
            iconClassName={'close'}
            sized={AppBtnSize.md}
            onClick={closePopup}
          />
          <AppBtn
            iconClassName={'save'}
            title={'Сохранить'}
            sized={AppBtnSize.md}
            color={AppBtnColor.violetWhite}
            onClick={onSaveHandler}
            isLoading={isLoadingCreate || isLoadingUpdate}
            disabled={isDisabled}
          />
        </div>
      </div>
    </Popup>
  )
}
